import type { Condition, ConditionGroup, ShowIfCondition, FormValues } from '../types';

/**
 * Normalize a value for case-insensitive string comparison in showIf/considerCompletedIf.
 * Used so that field mapping or Sitecore returning different casing (e.g. "Mirror wills")
 * still matches config literals (e.g. "Mirror Wills").
 */
function normalizeForStringCompare(val: unknown): unknown {
  if (typeof val === 'string') {
    return val.trim().toLowerCase();
  }
  return val;
}

/**
 * Compare left and right for eq/ne: use case-insensitive when both are strings.
 */
function valuesEqual(left: unknown, right: unknown): boolean {
  if (typeof left === 'string' && typeof right === 'string') {
    return normalizeForStringCompare(left) === normalizeForStringCompare(right);
  }
  return left === right;
}

/**
 * Check if left is in the right array: use case-insensitive match when both sides are strings.
 */
function valueInArray(left: unknown, right: unknown[]): boolean {
  return right.some((item) => {
    if (typeof left === 'string' && typeof item === 'string') {
      return normalizeForStringCompare(left) === normalizeForStringCompare(item);
    }
    return left === item;
  });
}

/**
 * Evaluates a single condition against form values
 */
export function evaluateCondition(condition: Condition, values: FormValues): boolean {
  const left = values[condition.fieldId];
  const right = condition.value;

  // Handle undefined values explicitly
  if (left === undefined) {
    // For most operators, undefined means the field hasn't been set, so condition fails
    // Exception: 'ne' (not equal) - undefined !== value is true
    if (condition.operator === 'ne') {
      return true; // undefined !== value is true
    }
    // For 'notIn', if value is undefined, we can't determine if it's "not in" the array
    // Return false to be safe (field hasn't been set yet)
    if (condition.operator === 'notIn') {
      return false;
    }
    // For all other operators, undefined means condition fails
    return false;
  }

  switch (condition.operator) {
    case 'eq':
      return valuesEqual(left, right);
    case 'ne':
      return !valuesEqual(left, right);
    case 'gt':
      return Number(left) > Number(right);
    case 'lt':
      return Number(left) < Number(right);
    case 'gte':
      return Number(left) >= Number(right);
    case 'lte':
      return Number(left) <= Number(right);
    case 'in':
      return Array.isArray(right) ? valueInArray(left, right) : false;
    case 'notIn':
      if (!Array.isArray(right)) {
        return false;
      }
      return !valueInArray(left, right);
    default:
      return true;
  }
}

/**
 * Evaluates a condition group (with explicit AND/OR operator)
 */
export function evaluateConditionGroup(group: ConditionGroup, values: FormValues): boolean {
  if (!group.conditions || group.conditions.length === 0) return true;

  const results = group.conditions.map((condition) => {
    if ('operator' in condition && 'conditions' in condition) {
      // It's a nested ConditionGroup
      return evaluateConditionGroup(condition as ConditionGroup, values);
    } else {
      // It's a Condition
      return evaluateCondition(condition as Condition, values);
    }
  });

  if (group.operator === 'AND') {
    return results.every((result) => result === true);
  } else if (group.operator === 'OR') {
    return results.some((result) => result === true);
  }

  return true;
}

/**
 * Evaluates conditions - supports both old array format and new group format
 */
export function evaluateConditions(
  conditions: ShowIfCondition | undefined,
  values: FormValues
): boolean {
  if (!conditions) return true;
  // New format: ConditionGroup object
  if (typeof conditions === 'object' && 'operator' in conditions && 'conditions' in conditions) {
    return evaluateConditionGroup(conditions as ConditionGroup, values);
  }

  // Old format: Condition array (backward compatibility)
  if (Array.isArray(conditions)) {
    const conditionArray = conditions as Condition[];
    if (conditionArray.length === 0) return true;
    // Group conditions by OR boundaries (old logic)
    const groups: Condition[][] = [];
    let currentGroup: Condition[] = [];

    for (const condition of conditionArray) {
      if (condition.or && currentGroup.length > 0) {
        // Start a new OR group
        groups.push(currentGroup);
        currentGroup = [condition];
      } else {
        currentGroup.push(condition);
      }
    }

    // Add the last group
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    // If no OR groups were found, evaluate all conditions with AND (original behavior)
    if (groups.length === 0 || (groups.length === 1 && !conditionArray.some((c) => c.or))) {
      return conditionArray.every((c) => evaluateCondition(c, values));
    }
    // Evaluate each OR group with AND, then combine with OR
    return groups.some((group) => {
      // Remove 'or' property for evaluation (it's just a marker)
      const groupConditions = group.map(({ or: _or, ...rest }) => rest);
      return groupConditions.every((c) => evaluateCondition(c, values));
    });
  }

  return true;
}

/**
 * Extracts the first fieldId from a ShowIfCondition for error messages
 * Supports both old array format and new group format
 */
export function getFirstFieldIdFromCondition(
  condition: ShowIfCondition | undefined
): string | undefined {
  if (!condition) return undefined;

  // New format: ConditionGroup object
  if (typeof condition === 'object' && 'operator' in condition && 'conditions' in condition) {
    const group = condition as ConditionGroup;
    if (group.conditions && group.conditions.length > 0) {
      const firstCondition = group.conditions[0];
      if ('fieldId' in firstCondition) {
        return (firstCondition as Condition).fieldId;
      } else if ('operator' in firstCondition && 'conditions' in firstCondition) {
        // Nested group - recursively get first fieldId
        return getFirstFieldIdFromCondition(firstCondition as ConditionGroup);
      }
    }
  }

  // Old format: Condition array
  if (Array.isArray(condition) && condition.length > 0) {
    return condition[0]?.fieldId;
  }

  return undefined;
}

/**
 * Extracts all fieldIds from a ShowIfCondition
 * Supports both old array format and new group format
 */
export function getAllFieldIdsFromCondition(condition: ShowIfCondition | undefined): string[] {
  if (!condition) return [];

  const fieldIds: string[] = [];

  // New format: ConditionGroup object
  if (typeof condition === 'object' && 'operator' in condition && 'conditions' in condition) {
    const group = condition as ConditionGroup;
    if (group.conditions) {
      group.conditions.forEach((cond) => {
        if ('fieldId' in cond) {
          // It's a Condition
          fieldIds.push((cond as Condition).fieldId);
        } else if ('operator' in cond && 'conditions' in cond) {
          // It's a nested ConditionGroup - recursively extract
          fieldIds.push(...getAllFieldIdsFromCondition(cond as ConditionGroup));
        }
      });
    }
  }

  // Old format: Condition array
  if (Array.isArray(condition)) {
    condition.forEach((cond) => {
      if (cond.fieldId) {
        fieldIds.push(cond.fieldId);
      }
    });
  }

  return fieldIds;
}
